import type { FeatureConfig } from '@skynet/feature-config';
import { GlobalOutlined } from '@ant-design/icons';
import { REGION_DETAIL_ROUTE_PATH, REGIONS_LIST_URL, REGIONS_ROUTE_PATH } from './routes';

/**
 * Feature manifest for the admin Regions list and region detail pages. The dashboard shell reads
 * this single source of truth instead of hardcoding the route, navigation
 * entry, header label and Cerbos action across its routing, navigation,
 * header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing this config for its
 * nav/header metadata never pulls the page implementation into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this package's own
 * `./routes`, which also supplies the URLs the page hands to `@skynet/sky-region`'s
 * components. The config imports nothing from `@skynet/sky-region`, so reading it
 * never drags the Regions UI onto the boot path.
 * `section`, `nav.key` and `cerbosAction` stay literal: they are the identifier
 * the Cerbos policy is written against (`read:admin:regions`), and renaming any
 * of them hides the nav item until the policy is renamed too.
 */
export const regionsConfig = {
  domain: 'Admin',
  section: 'regions',
  route: {
    path: REGIONS_ROUTE_PATH,
    load: async () => ({ default: (await import('./Regions')).Regions }),
  },
  detailRoute: {
    path: REGION_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./RegionPage')).RegionPage }),
  },
  nav: {
    key: 'admin.regions',
    label: 'Regions',
    icon: <GlobalOutlined />,
    url: REGIONS_LIST_URL,
  },
  header: { label: 'REGIONS' },
  cerbosAction: 'read:admin:regions',
  skipWsLoginPaths: [REGIONS_LIST_URL],
} as const satisfies FeatureConfig;
